// Utilidad para precargar el logo de la marca como Data URL PNG.
//
// ¿Por qué? La generación de los PDF (comprobante y guía de envío) es 100%
// SÍNCRONA para conservar el "gesto del usuario" del clic. jsPDF necesita los
// BYTES reales de la imagen (no una URL) para poder dibujarla con addImage().
// Por eso el logo se descarga, se convierte a Data URL PNG y se cachea en
// memoria ANTES del clic (cuando la página/modal monta). Así, al pulsar
// "Descargar", el PDF ya tiene el logo listo sin ningún await de por medio.

export interface BrandLogo {
  dataUrl: string;
  width: number;
  height: number;
}

let cachedSourceUrl: string | null = null;
let cachedLogo: BrandLogo | null = null;
let inFlight: Promise<BrandLogo | null> | null = null;

// Devuelve el logo cacheado de forma síncrona (o null si aún no está listo).
export function getBrandLogo(): BrandLogo | null {
  return cachedLogo;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo cargar el logo'));
    img.src = src;
  });
}

// Descarga el logo, lo normaliza a PNG (vía canvas, preservando transparencia)
// y limita su tamaño para que el PDF no se infle.
async function fetchLogo(url: string): Promise<BrandLogo | null> {
  try {
    const res = await fetch(url, { mode: 'cors', cache: 'force-cache' });
    if (!res.ok) return null;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const img = await loadImage(objectUrl);
      const naturalW = img.naturalWidth || img.width;
      const naturalH = img.naturalHeight || img.height;
      if (!naturalW || !naturalH) return null;

      const maxDim = 600;
      const scale = Math.min(1, maxDim / Math.max(naturalW, naturalH));
      const width = Math.max(1, Math.round(naturalW * scale));
      const height = Math.max(1, Math.round(naturalH * scale));

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/png');
      return { dataUrl, width, height };
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    return null;
  }
}

// Precarga y cachea el logo. Si la URL no cambió y ya está cacheado, resuelve
// al instante. Devuelve el logo normalizado o null si falla.
export function preloadBrandLogo(url: string | null | undefined): Promise<BrandLogo | null> {
  if (!url) {
    cachedSourceUrl = null;
    cachedLogo = null;
    return Promise.resolve(null);
  }
  if (url === cachedSourceUrl && cachedLogo) return Promise.resolve(cachedLogo);
  if (inFlight && url === cachedSourceUrl) return inFlight;

  cachedSourceUrl = url;
  cachedLogo = null;
  inFlight = fetchLogo(url).then((logo) => {
    if (cachedSourceUrl === url) cachedLogo = logo;
    inFlight = null;
    return logo;
  });
  return inFlight;
}